import React, { Component } from "react";
import "./App.scss";
import Search from "./components/Search.js";
import Results from "./components/Results.js";

import { ITEMS_PER_PAGE, fetchFormOptions, searchForMajors } from "./api";

import {
  validateSearchFormParams,
  parseQueryString,
  formatSearchParamsAsQueryString
} from "./utils";

class App extends Component {
  constructor(props) {
    super(props);

    this.searchParams = {};

    this.state = {
      isLoadingOptions: true,
      isSearching: false,
      initialParams: null, // this is used to pass some initial values to the form if we have them
      searchResults: [],
      pageInfo: {},
      formOptions: {
        campusOptions: [],
        applicantLevelOptions: [],
        termOptions: [],
        categoryOptions: [],
        years: []
      },
      networkError: null
    };

    // attempt to parse query string from url and set initialParams and searchParams
    const queryString = window.location.search;
    if (queryString) {
      try {
        const initialParams = parseQueryString(queryString);
        if (initialParams.applicant_level) {
          initialParams.applicantLevel = initialParams.applicant_level;
          delete initialParams.applicant_level;
        }
        if (initialParams.major_name) {
          initialParams.majorName = initialParams.major_name;
          delete initialParams.major_name;
        }
        if (initialParams.show_closed) {
          initialParams.showClosed = initialParams.show_closed;
          delete initialParams.show_closed;
        }
        const errors = validateSearchFormParams(initialParams);
        if (errors.length > 0) {
          throw new Error("initial params are invalid!");
        }
        // we set our params to the initial params right away so we can start a search

        this.searchParams = initialParams;
        this.state.initialParams = initialParams;
      } catch (e) {
        // if this gets messed up (bad query string?) then reset back to our standard starting state
        this.searchParams = {};
        this.state.initialParams = null;
      }
    }
  }

  componentDidMount() {
    fetchFormOptions()
      .then(formOptions => {
        if (this.hasParams) {
          // if the page prop is undefined it will default to 1 in the search function
          this.search(this.searchParams.page, true);
        }

        this.setState({
          formOptions: {
            campusOptions: formOptions.campuses,
            applicantLevelOptions: formOptions.applicantLevels,
            termOptions: formOptions.terms,
            categoryOptions: formOptions.categories,
            years: formOptions.yearsList
          }
        });
      })
      .catch(error => {
        this.setState({ networkError: error });
      })
      .finally(() => {
        this.setState({ isLoadingOptions: false });
      });
  }

  get hasOptions() {
    return Object.keys(this.state.formOptions).length > 0;
  }

  get hasParams() {
    return Object.keys(this.searchParams).length > 0;
  }

  get hasResults() {
    return Object.keys(this.state.pageInfo).length > 0;
  }

  // we want to return a {} by default because we are accessing params on this value elsewhere
  get searchResultsApplicantLevel() {
    if (!(this.hasParams && this.hasOptions)) {
      return {};
    }

    const levelId = this.searchParams.applicantLevel;
    const levelData = this.state.formOptions.applicantLevelOptions;

    const currentLevel = levelData.find(level => level.id === levelId);
    return currentLevel ? currentLevel : {};
  }

  get formattedSearchParams() {
    const {
      applicantLevel,
      campuses,
      terms,
      majorName,
      category,
      showClosed
    } = this.searchParams;

    const params = {
      applicant_level: applicantLevel
    };

    if (campuses && campuses.length) {
      params.campuses = Array.from(campuses);
    }

    if (terms && terms.length) {
      params.terms = Array.from(terms);
    }

    if (majorName) {
      params.major_name = majorName;
    }

    if (category) {
      params.category = category;
    }

    if (showClosed) {
      params.show_closed = showClosed;
    }

    return params;
  }

  getFormDataAndSearch = formData => {
    this.searchParams = { ...formData };
    this.search(1, true);
  };

  search = (page = 1) => {
    // save params as query string
    const queryString = formatSearchParamsAsQueryString({
      ...this.formattedSearchParams,
      page
    });
    window.history.pushState({}, "", queryString);

    const params = {
      ...this.formattedSearchParams,
      page,
      items_per_page: ITEMS_PER_PAGE
    };
    // we need to also include term 3 (Spring) if the user searches for term 2 (Winter/Spring)
    if (params.terms && params.terms.find(term => term === 2)) {
      params.terms.push(3);
    }

    this.setState({ isSearching: true, networkError: null });

    searchForMajors(params)
      .then(res => {
        this.setState({
          searchResults: res.data,
          pageInfo: res.meta,
          isSearching: false
        });
      }).catch(error => {
        this.setState( {
          networkError: error,
          isSearching: false
        });
      });
  };

  render() {
    const {
      isSearching,
      isLoadingOptions,
      formOptions,
      initialParams,
      networkError,
      searchResults,
      pageInfo
    } = this.state;
    return (
      <div className="App container-hybrid main" >
        <div className="container ugm-container">

          <Search
            isSearching={isSearching}
            isLoadingOptions={isLoadingOptions}
            formOptions={formOptions}
            onSubmit={this.getFormDataAndSearch}
            initialParams={initialParams}
            networkError={networkError}
          />
          {(this.hasResults || isSearching) && (
            <Results
              hasResults={this.hasResults}
              applicantLevel={this.searchResultsApplicantLevel}
              isSearching={isSearching}
              results={searchResults}
              pageInfo={pageInfo}
              searchFunction={this.search}
              years={formOptions.years}
            />
          )}
        </div>
      </div>
    );
  }
}

export default App;
